import React from 'react';
import { Link } from 'react-router-dom';
import { ArrowRight, CheckCircle2, Plane, TrendingUp, BookOpen, Globe, Award, DollarSign } from 'lucide-react';

const EliteTransformationPage = () => {
  return (
    <div className="dark-page">
      {/* Hero Section */}
      <section className="page-hero elite-hero">
        <div className="page-hero-content">
          <div className="elite-super-badge">PREMIUM DONE-FOR-YOU SERVICE</div>
          <h1 className="display-huge page-title">Elite Business Transformation</h1>
          <p className="body-large page-subtitle">
            I Fly To You. We Build Together. You Launch In 90 Days.
          </p>
          <p className="body-medium page-description">
            This isn't coaching. This isn't consulting. This is me physically coming to you, rolling up my sleeves, and building your empire alongside you—from zero to $100K+ in 90 days, guaranteed.
          </p>
        </div>
      </section>

      {/* What This Is */}
      <section className="elite-what-section">
        <div className="section-header">
          <h2 className="display-large section-title">What Is This?</h2>
        </div>

        <div className="elite-what-content">
          <p className="body-large elite-what-text">
            <strong>Elite Business Transformation</strong> is a complete business acceleration unlike anything else. Within 5 days of signing up, I board a plane to your location. We spend intensive days together where I don't just tell you what to do—I do it with you, for you, and teach you every step so you own the process forever.
          </p>
          <p className="body-large elite-what-text">
            Whether you're starting from scratch or restructuring an existing business, I architect every piece: your entity structure, your website, your book, your credit infrastructure, your social media presence, your funding positioning, and your revenue streams. This is business on steroids with a guarantee: <strong>$100,000+ in 90 days or we keep working until you hit it.</strong>
          </p>
        </div>
      </section>

      {/* What's Included */}
      <section className="elite-included-section">
        <div className="section-header">
          <h2 className="display-large section-title">Everything Included</h2>
          <p className="body-medium section-description">
            This is the most comprehensive business transformation package ever created
          </p>
        </div>

        <div className="elite-included-grid">
          <div className="elite-included-card">
            <div className="elite-card-icon">
              <Plane size={40} />
            </div>
            <h3 className="heading-2 elite-card-title">On-Site Build-Out</h3>
            <p className="body-medium elite-card-description">
              I fly to you within 5 days of sign-up. We work together intensively to build your complete business infrastructure from A-Z.
            </p>
          </div>

          <div className="elite-included-card">
            <div className="elite-card-icon">
              <Globe size={40} />
            </div>
            <h3 className="heading-2 elite-card-title">Custom Website</h3>
            <p className="body-medium elite-card-description">
              Professional, conversion-optimized website designed and built while we're together. Live before I leave.
            </p>
          </div>

          <div className="elite-included-card">
            <div className="elite-card-icon">
              <BookOpen size={40} />
            </div>
            <h3 className="heading-2 elite-card-title">75-Page Book + Publishing</h3>
            <p className="body-medium elite-card-description">
              I design, write, and publish your hardcover and paperback book on Amazon. Instant authority and credibility.
            </p>
          </div>

          <div className="elite-included-card">
            <div className="elite-card-icon">
              <Award size={40} />
            </div>
            <h3 className="heading-2 elite-card-title">5 Premium Tradelines</h3>
            <p className="body-medium elite-card-description">
              1 Primary Tradeline ($10K) + 4 Authorized User Tradelines = $200,000 in total credit limits for funding power.
            </p>
          </div>

          <div className="elite-included-card">
            <div className="elite-card-icon">
              <TrendingUp size={40} />
            </div>
            <h3 className="heading-2 elite-card-title">Viral Post Service</h3>
            <p className="body-medium elite-card-description">
              Guaranteed 1,000,000+ Instagram reel views in 15 days. Explosive social media growth for your brand.
            </p>
          </div>

          <div className="elite-included-card">
            <div className="elite-card-icon">
              <DollarSign size={40} />
            </div>
            <h3 className="heading-2 elite-card-title">Revenue Stream Guides</h3>
            <p className="body-medium elite-card-description">
              Medical Courier Business Guide, Settlement App Guide, and $0 Down Property Acquisition Guide—multiple income paths.
            </p>
          </div>
        </div>
      </section>

      {/* The Guarantee */}
      <section className="elite-guarantee-section">
        <div className="elite-guarantee-content">
          <h2 className="display-large elite-guarantee-title">The $100K Guarantee</h2>
          <p className="body-large elite-guarantee-text">
            I guarantee you'll generate <strong>$100,000+ in revenue within 90 days</strong> of completing the program. If you don't hit this milestone, I continue working with you—at no additional cost—until you do. This isn't a refund. This is a commitment to your success.
          </p>
          <p className="body-medium elite-guarantee-note">
            This guarantee applies when you implement the strategies and systems we build together. Your success is my success.
          </p>
        </div>
      </section>

      {/* Why This Works */}
      <section className="elite-why-section">
        <div className="section-header">
          <h2 className="display-large section-title">Why This Works</h2>
        </div>

        <div className="elite-why-grid">
          <div className="elite-why-card">
            <h3 className="heading-2 elite-why-title">You Learn By Doing</h3>
            <p className="body-medium elite-why-description">
              This isn't a course. I build everything with you, in real-time, explaining every decision so you understand the "why" behind every move.
            </p>
          </div>

          <div className="elite-why-card">
            <h3 className="heading-2 elite-why-title">Complete Infrastructure</h3>
            <p className="body-medium elite-why-description">
              Most people fail because they're missing pieces. We build the COMPLETE system: structure, credit, marketing, products, positioning, and revenue streams.
            </p>
          </div>

          <div className="elite-why-card">
            <h3 className="heading-2 elite-why-title">Proven Frameworks</h3>
            <p className="body-medium elite-why-description">
              Every strategy I implement has generated millions for my clients. You get battle-tested systems, not theory.
            </p>
          </div>

          <div className="elite-why-card">
            <h3 className="heading-2 elite-why-title">Speed + Support</h3>
            <p className="body-medium elite-why-description">
              No waiting months for results. We build fast, launch fast, and scale fast. Plus, you have ongoing support throughout your 90-day journey.
            </p>
          </div>
        </div>
      </section>

      {/* Who This Is For */}
      <section className="elite-for-section">
        <div className="section-header">
          <h2 className="display-large section-title">Is This For You?</h2>
        </div>

        <div className="elite-for-content">
          <div className="elite-for-yes">
            <h3 className="heading-2 elite-for-heading">This Is For You If:</h3>
            <ul className="elite-for-list">
              <li className="elite-for-item">
                <CheckCircle2 size={20} />
                <span>You're ready to invest in building a real, fundable business</span>
              </li>
              <li className="elite-for-item">
                <CheckCircle2 size={20} />
                <span>You want hands-on, done-with-you implementation, not just advice</span>
              </li>
              <li className="elite-for-item">
                <CheckCircle2 size={20} />
                <span>You're committed to executing the systems we build together</span>
              </li>
              <li className="elite-for-item">
                <CheckCircle2 size={20} />
                <span>You understand the value of speed, expertise, and proven frameworks</span>
              </li>
              <li className="elite-for-item">
                <CheckCircle2 size={20} />
                <span>You're ready to hit $100K+ in 90 days and scale from there</span>
              </li>
            </ul>
          </div>

          <div className="elite-for-no">
            <h3 className="heading-2 elite-for-heading">This Is NOT For You If:</h3>
            <ul className="elite-for-list elite-for-list-no">
              <li className="elite-for-item">
                <span>You're looking for a cheap, do-it-yourself course</span>
              </li>
              <li className="elite-for-item">
                <span>You're not willing to take action and implement</span>
              </li>
              <li className="elite-for-item">
                <span>You expect results without putting in the work</span>
              </li>
              <li className="elite-for-item">
                <span>You're not serious about building a legitimate business</span>
              </li>
            </ul>
          </div>
        </div>
      </section>

      {/* Investment */}
      <section className="elite-investment-section">
        <div className="elite-investment-content">
          <h2 className="display-large elite-investment-title">Investment</h2>
          <div className="elite-price-display">$10,000</div>
          <p className="body-large elite-investment-text">
            The <strong>Elite Business Transformation</strong> package includes everything you need to build, launch, and scale to $100K+ in 90 days. This is a complete done-for-you and done-with-you business acceleration system.
          </p>
          <p className="body-medium elite-investment-note">
            Payment plans available. This is for serious entrepreneurs only—spots are extremely limited.
          </p>
        </div>
      </section>

      {/* CTA Section */}
      <section className="cta-section elite-cta">
        <div className="cta-content">
          <h2 className="display-large cta-title">Ready to Transform Your Business?</h2>
          <p className="body-large cta-description">
            Schedule your consultation call now. Within 5 days of approval, I'm on a plane to you.
          </p>
          <div className="elite-cta-buttons">
            <a 
              href="https://www.fanbasis.com/agency-checkout/THEACADEMYCEO/Ro3Ez" 
              target="_blank" 
              rel="noopener noreferrer"
              className="btn-primary cta-button elite-buy-button"
            >
              Learn More - $10,000
            </a>
            <a 
              href="https://calendly.com/thecollinsacademynew/30min" 
              target="_blank" 
              rel="noopener noreferrer" 
              className="btn-secondary cta-button"
            >
              Schedule Consultation
              <ArrowRight size={20} />
            </a>
          </div>
          <p className="cta-note body-small">
            Limited availability - serious applicants only
          </p>
        </div>
      </section>
    </div>
  );
};

export default EliteTransformationPage;
